/**
 * V5 报告 API 客户端。
 *
 * **后端权威 contract**（docs/archive/v5/M5-integration-contract.md §API Contract，2026-05-20）:
 *   GET  /api/reports/{assessment_id}/            → 200 done | 202 generating | 500 failed | 404/400 pre-flight
 *   POST /api/reports/{assessment_id}/            → 202 generating（重新生成；幂等）
 *   GET  /api/reports/{assessment_id}/download/   → FileResponse(PDF)，同步生成 1-3s
 *
 * 状态枚举：done | generating | failed（没有 fallback 状态；fallback 通过
 * body.is_fallback=true 表达，HTTP 仍是 200 + status=done）。
 *
 * 本文件被设计成「换 endpoint / 换 wrapper 时只改这里」：
 *  - URL 走 env（REACT_APP_REPORT_V5_ENDPOINT）+ DEFAULT_ENDPOINT 兜底
 *  - mock 模式（assessmentId 为 'preview' / 'demo' 时自动启用）：返回 mock raw payload
 *  - generating / failed / 4xx 分别归一化到调用方的 status state
 *
 * 调用方应通过 adapters/reportV5Adapter.js 把 raw 转 view model，
 * 不要在组件里直接消费 raw。
 */
import client from './client';
import {
  mockReportRawPayload,
  mockReportFallbackPayload,
  mockReportGeneratingPayload,
} from '../mocks/v5ReportMock';
import { mockReportDetailedRawPayload } from '../mocks/v5ReportDetailedMock';

// ---- Content-Disposition filename helper ----------------------------------
/**
 * 解析 Content-Disposition header 拿文件名。
 *
 * 优先级:
 * 1. filename*=UTF-8''<urlencoded> (RFC 5987, 支持中文)
 * 2. filename="<ascii>" (老浏览器兼容)
 * 3. 返回 null (caller 用 fallback)
 *
 * 例:
 *   Content-Disposition: attachment; filename*=UTF-8''AI%E9%A2%86%E5%AF%BC%E5%8A%9B%E6%B5%8B%E8%AF%84%E6%8A%A5%E5%91%8A%EF%BC%88%E5%9F%BA%E7%A1%80%E7%89%88%EF%BC%89.pdf; filename="report.pdf"
 *   → 解析返回 'AI领导力测评报告（基础版）.pdf'
 */
function parseContentDispositionFilename(header) {
  if (!header) return null;
  // 优先 filename* (RFC 5987)
  const utf8Match = header.match(/filename\*\s*=\s*([^']+)''([^;]+)/i);
  if (utf8Match) {
    const charset = utf8Match[1].toUpperCase();
    if (charset === 'UTF-8') {
      try { return decodeURIComponent(utf8Match[2].trim().replace(/^"|"$/g, '')); }
      catch (_) { /* fall through */ }
    }
  }
  // fallback 到 filename=
  const asciiMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (asciiMatch) return asciiMatch[1].trim();
  return null;
}

// ---- endpoint config ------------------------------------------------------
// 注：client.js 的 BASE_URL 已含 '/api'。这里只写 path 后缀。
const DEFAULT_ENDPOINT = '/reports';
function resolveEndpoint(assessmentId, suffix = '') {
  const envBase = process.env.REACT_APP_REPORT_V5_ENDPOINT;
  const base = (envBase || DEFAULT_ENDPOINT).replace(/\/$/, '');
  return suffix
    ? `${base}/${assessmentId}/${suffix.replace(/^\//, '')}`
    : `${base}/${assessmentId}/`;
}

// ---- status enum ----------------------------------------------------------
export const REPORT_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  GENERATING: 'generating',
  DONE: 'done',
  FAILED: 'failed',     // unrecoverable backend error → 显示 retry CTA
  ERROR: 'error',       // 4xx 之类的 pre-flight 错误（assessment 不存在 / 未完成）
});

const RAW_STATUS_MAP = {
  done: REPORT_STATUS.DONE,
  generating: REPORT_STATUS.GENERATING,
  failed: REPORT_STATUS.FAILED,
};
function normalizeStatus(rawStatus) {
  if (!rawStatus) return REPORT_STATUS.DONE; // 后端约定：legacy 响应无 status 字段 = done
  return RAW_STATUS_MAP[String(rawStatus).toLowerCase()] || REPORT_STATUS.DONE;
}

// ---- mock -----------------------------------------------------------------
async function fetchMock(variant = 'done') {
  await new Promise((r) => setTimeout(r, 250)); // 模拟轻微延迟
  if (variant === 'fallback') {
    return { status: REPORT_STATUS.DONE, payload: mockReportFallbackPayload, error: null, canRetry: false };
  }
  if (variant === 'generating') {
    return { status: REPORT_STATUS.GENERATING, payload: mockReportGeneratingPayload, error: null, canRetry: false };
  }
  return { status: REPORT_STATUS.DONE, payload: mockReportRawPayload, error: null, canRetry: false };
}

// ---- normalize HTTP response ---------------------------------------------
function fromAxiosResponse(data) {
  const status = normalizeStatus(data?.status);
  if (status === REPORT_STATUS.GENERATING) {
    // backend ?variant=detailed 轮询 phase：'rough_pending' / 'detailed_pending'。
    // 粗略 endpoint 一般无 phase 字段，或仅为简单 'pending'。
    return {
      status, payload: null, error: null, canRetry: false,
      phase: typeof data?.phase === 'string' ? data.phase : null,
    };
  }
  // done（含 is_fallback=true）走这里
  return { status, payload: data, error: null, canRetry: false, phase: null };
}

function fromAxiosError(err) {
  const resp = err?.response;
  // 500 / 409 failed → 后端约定 body.status='failed' + detail + can_retry。
  // - 500: 生成失败（detailed 端 detail 由后端从 quality.detailed_error 复制过来）
  // - 409: ?variant=detailed 命中 legacy V2/V4 报告的极端场景（M5.5 P2 fix）。
  //        body 形态同 500（含 report_version=legacy 版本号），同样 can_retry=true。
  //        retry CTA 走 POST 同 endpoint 会强制翻 V5，下次 ?variant=detailed 走正常路径。
  if ((resp?.status === 500 || resp?.status === 409) && resp.data?.status === 'failed') {
    return {
      status: REPORT_STATUS.FAILED,
      payload: null,
      error: resp.data?.detail || '报告生成失败',
      canRetry: Boolean(resp.data?.can_retry),
      phase: null,
    };
  }
  // 4xx 前置错误：测评不存在 / 未完成 / 详细报告未解锁（detailed 端 403）
  if (resp?.status === 404 || resp?.status === 400 || resp?.status === 403) {
    return {
      status: REPORT_STATUS.ERROR,
      payload: null,
      error: resp.data?.detail || '报告无法加载',
      canRetry: false,
      phase: null,
    };
  }
  // 其他网络错误 / 5xx 非约定形态
  return {
    status: REPORT_STATUS.ERROR,
    payload: null,
    error: resp?.data?.detail || err?.message || '报告加载失败',
    canRetry: false,
    phase: null,
  };
}

// ---- real -----------------------------------------------------------------
async function fetchReal(assessmentId) {
  try {
    const { data } = await client.get(resolveEndpoint(assessmentId));
    return fromAxiosResponse(data);
  } catch (err) {
    return fromAxiosError(err);
  }
}

async function regenerateReal(assessmentId) {
  try {
    const { data } = await client.post(resolveEndpoint(assessmentId));
    return fromAxiosResponse(data);
  } catch (err) {
    return fromAxiosError(err);
  }
}

// ---- public API -----------------------------------------------------------
/**
 * @returns {Promise<{status,payload,error,canRetry}>}
 */
export async function fetchReportV5(assessmentId, opts = {}) {
  const useMock = opts.mock ?? (assessmentId === 'preview' || assessmentId === 'demo');
  if (useMock) return fetchMock(opts.mockVariant);
  return fetchReal(assessmentId);
}

/**
 * 最终复测后的逐题、逐选项解析。
 *
 * 409 formal_retest_required 是正常的产品门槛状态，不作为通用加载错误展示。
 */
export async function fetchQuestionAnalysis(assessmentId, opts = {}) {
  const useMock = opts.mock ?? (assessmentId === 'preview' || assessmentId === 'demo');
  if (useMock) {
    return {
      status: 'locked',
      payload: null,
      error: '完成正式复测后开放完整题目与选项解析',
      analysisAccess: {
        level: 'ability_only',
        full_explanations_unlocked: false,
        unlock_condition: 'formal_retest_completed',
      },
    };
  }

  try {
    const { data } = await client.get(
      resolveEndpoint(assessmentId, 'question-analysis/'),
      { signal: opts.signal },
    );
    return {
      status: 'done',
      payload: data,
      error: null,
      analysisAccess: data?.analysis_access || null,
    };
  } catch (err) {
    const response = err?.response;
    if (response?.status === 409 && response.data?.code === 'formal_retest_required') {
      return {
        status: 'locked',
        payload: null,
        error: response.data?.detail || '完成正式复测后开放完整题目与选项解析',
        analysisAccess: response.data?.analysis_access || null,
      };
    }
    if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
      return { status: 'cancelled', payload: null, error: null, analysisAccess: null };
    }
    return {
      status: 'error',
      payload: null,
      error: response?.data?.detail || err?.message || '逐题解析加载失败',
      analysisAccess: null,
    };
  }
}

/**
 * 详细报告（付费 6-Page 版）GET。
 *
 * Endpoint（**等后端 ack docs/archive/v5/AI-HANDOFF.md 2026-05-20 14:00 提议**）：
 *   GET /api/reports/{id}/?variant=detailed
 *
 * 当 schema 落地前，preview/demo 走 mock；真 id 走真接口（响应里
 * `user_report_v5.detailed` 子块存在 = 已解锁）。
 *
 * 未解锁 → 后端约定返回 403 detail，前端走 error 态 + 在 UI 上引导付费。
 */
async function fetchDetailedReal(assessmentId) {
  try {
    const url = resolveEndpoint(assessmentId);
    const { data } = await client.get(url, { params: { variant: 'detailed' } });
    return fromAxiosResponse(data);
  } catch (err) {
    return fromAxiosError(err);
  }
}

async function fetchDetailedMock() {
  await new Promise((r) => setTimeout(r, 250));
  return { status: REPORT_STATUS.DONE, payload: mockReportDetailedRawPayload, error: null, canRetry: false };
}

export async function fetchReportV5Detailed(assessmentId, opts = {}) {
  const useMock = opts.mock ?? (assessmentId === 'preview' || assessmentId === 'demo');
  if (useMock) return fetchDetailedMock();
  return fetchDetailedReal(assessmentId);
}

/**
 * 触发后端重新生成（POST 同一 endpoint，返回 202 generating）。
 * 调用方在 GET 拿到 failed 后，用此发起重试，然后恢复 polling。
 */
export async function regenerateReportV5(assessmentId, opts = {}) {
  const useMock = opts.mock ?? (assessmentId === 'preview' || assessmentId === 'demo');
  if (useMock) return fetchMock('generating');
  return regenerateReal(assessmentId);
}

/**
 * 触发 PDF 下载（feat/report-pdf-redesign-20260527 2026-05-27）。
 * 后端: GET /api/reports/{id}/download/pdf/?variant=rough|detailed
 *   - variant='rough' (默认): ReportV5 粗略 3 页,文件名 AI领导力测评报告（基础版）.pdf
 *   - variant='detailed':     ReportV5Detailed 详细 6 页,文件名 AI领导力测评报告（专业版）.pdf
 * 同步生成 (首次 5-8s, 命中缓存 <1s), 返回 application/pdf (真正矢量 PDF,
 * 文字可选可搜). mock 模式不支持下载.
 *
 * 与同模块 downloadReportV5PNG() 完全平行的 sibling。重写自老 ReportLab
 * /download/ 链路 (走 PNG helper 同款 pattern: variant + 120s timeout +
 * Blob 错误解析)。
 */
export async function downloadReportV5PDF(assessmentId, opts = {}) {
  const useMock = opts.mock ?? (assessmentId === 'preview' || assessmentId === 'demo');
  if (useMock) {
    return { ok: false, error: '预览模式不支持 PDF 下载（无真实 assessment id）' };
  }
  const variant = opts.variant === 'detailed' ? 'detailed' : 'rough';

  try {
    const resp = await client.get(resolveEndpoint(assessmentId, 'download/pdf/'), {
      responseType: 'blob',
      params: { variant },
      // PDF 生成: Playwright 启动 Chromium + 渲染 + page.pdf 首次冷跑 5-8s,
      // 详细报告 6 页稍慢. client 默认 30s 不够, 对齐 backend Gunicorn /
      // nginx proxy_read_timeout 给 120s.
      timeout: 120000,
    });
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // 优先用后端 Content-Disposition (Phase 2 班级/企业 PDF 文件名同款链路);
    // fallback 用前端拼接 (老 PNG 链路 / 后端漏设 header 兜底)。
    const cd = resp.headers['content-disposition'] || resp.headers['Content-Disposition'];
    // fallback 与屏上标题 / 后端 filename_for_variant 同名（基础版/专业版）。
    a.download = parseContentDispositionFilename(cd)
      || `AI领导力测评报告（${variant === 'detailed' ? '专业版' : '基础版'}）.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (err) {
    // blob 响应 4xx/5xx 时 err.response.data 是 Blob, 需要文本化才能拿 detail
    // (与 PNG helper 同款解析)
    let detail = null;
    const respData = err?.response?.data;
    if (respData instanceof Blob) {
      try {
        const text = await respData.text();
        const parsed = JSON.parse(text);
        detail = parsed?.detail || null;
      } catch (_) {
        detail = null;
      }
    } else if (respData && typeof respData === 'object') {
      detail = respData.detail || null;
    }
    return { ok: false, error: detail || err?.message || 'PDF 下载失败' };
  }
}

/**
 * 触发 PNG 长图下载（feat/pdf-playwright-frontend 2026-05-22 替代 PDF 作主入口）。
 * 后端: GET /api/reports/{id}/download/png/?variant=rough|detailed
 *   - variant='rough' (默认): ReportV5 粗略 3 页, 文件名 AI领导力测评报告（基础版）.png
 *   - variant='detailed':    ReportV5Detailed 详细 6 页, 文件名 AI领导力测评报告（专业版）.png
 * 同步生成 (首次 5-10s, 命中缓存 <1s), 返回 image/png. mock 模式不支持下载.
 */
export async function downloadReportV5PNG(assessmentId, opts = {}) {
  const useMock = opts.mock ?? (assessmentId === 'preview' || assessmentId === 'demo');
  if (useMock) {
    return { ok: false, error: '预览模式不支持下载（无真实 assessment id）' };
  }
  const variant = opts.variant === 'detailed' ? 'detailed' : 'rough';
  try {
    const resp = await client.get(resolveEndpoint(assessmentId, 'download/png/'), {
      responseType: 'blob',
      params: { variant },
      // PNG 生成: Playwright 启动 Chromium + 渲染前端 + screenshot 首次冷
      // 跑 10-30s, 详细报告 6 页更慢. client 默认 30s 不够, 对齐 backend
      // Gunicorn / nginx proxy_read_timeout 给 120s.
      timeout: 120000,
    });
    const blob = new Blob([resp.data], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // PNG 文件名与屏上标题 / PDF 下载同名（基础版/专业版），仅后缀不同。
    a.download = `AI领导力测评报告（${variant === 'detailed' ? '专业版' : '基础版'}）.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (err) {
    // blob 响应 4xx/5xx 时 err.response.data 是 Blob, 需要文本化才能拿 detail
    let detail = null;
    const respData = err?.response?.data;
    if (respData instanceof Blob) {
      try {
        const text = await respData.text();
        const parsed = JSON.parse(text);
        detail = parsed?.detail || null;
      } catch (_) {
        detail = null;
      }
    } else if (respData && typeof respData === 'object') {
      detail = respData.detail || null;
    }
    return { ok: false, error: detail || err?.message || '报告下载失败' };
  }
}

/**
 * 详细报告解锁（付费）—— M5 范围外，纯前端占位。
 * TODO(post-M5)：付费 ticket 启动后，把此函数指向真接口（含 webhook 回调对账）。
 */
export async function unlockDetailedReport(assessmentId) { // eslint-disable-line no-unused-vars
  await new Promise((r) => setTimeout(r, 200));
  return { unlocked: true };
}
